/**
 * The single icon source for the app: Phosphor icons, re-exported from the
 * SSR-safe entry so they render in both server and client components (no
 * IconContext needed). No hand-rolled SVG, no emoji/unicode glyphs anywhere;
 * import what you need from here.
 *
 * Icons inherit size and colour from CSS: Phosphor's default size is `1em`, so
 * `font-size` on the wrapper sizes the glyph and `color` (via currentColor) tints
 * it. Keep that contract, so icons theme like text.
 */
export {
  // navigation / shell
  House, // Work
  Kanban, // a board; a stage value's sign (three columns, src/components/values/)
  ListChecks, // the checklist rail (docs/CHECKLIST.md)
  LockSimple, // a checklist line the viewer may see, not act on; an item the viewer cannot see
  Table, // a list view (the columnar table)
  Columns, // the Columns dialog of a list: which columns show, in what order
  Graph, // the Map (dependency graph)
  SquaresFour, // Views (the lens index; the compact dock's Views lane)
  Aperture, // a lens (the way-of-looking kind, docs/LENSES.md)
  CalendarBlank, // the schedule module's at-rest face; a date value's sign; the Schedule door on the phone item page
  Steps, // Stages (the stage-gate portfolio)
  Path, // group by: Stage (a place along a flow's stages)
  Stack, // Plans (a collection of staged proposals); the Resources door on the phone item page
  Bell, // Activity (the notifications surface); per-item "default" notify
  BellRinging, // per-item "follow" (see all its activity)
  BellSlash, // mute an item's notifications
  Tray, // empty inbox ("all caught up")
  MagnifyingGlass, // the quick switcher (Search)
  TerminalWindow, // the palette's command mode (Ctrl+P / ">")
  CaretDoubleLeft, // collapse sidebar
  CaretDoubleRight, // expand sidebar
  CaretDoubleUp, // collapse all sections
  CaretDoubleDown, // expand all sections
  DotsSixVertical, // the grip of a sortable line (the checklist rail's drag handle)
  HandPointing, // a manual order in force (the Group by menu's active grouping, sections ranked by hand)
  // disclosure / carets
  CaretRight, // closed disclosure, breadcrumb separator
  ArrowRight, // a change "from -> to" (status, field)
  CaretDown, // open disclosure, dropdown, "below" rollup; reorder a row down
  CaretUp, // reorder a row up (stage-row editor)
  CaretLeft, // back
  // the decomposition parent path on a card
  ArrowUp, // parent (one hop up)
  ArrowUDownLeft, // fold a pulled child back
  ArrowSquareOut, // pull a child out to the board
  // window controls
  ArrowsOut, // enter fullscreen
  ArrowsIn, // exit fullscreen
  X, // close / remove; the next-step ladder's Cancel
  Check, // selected / confirmed; the next-step ladder's Close
  // the next-step ladder's other rungs (docs/CLOSED_AXIS.md)
  Play, // Start: declare the work active
  PaperPlaneTilt, // Deliver: hand the work over for review
  Pause, // Idle: stand the work down again
  // the requester's judgment over a standing delivery (docs/CLOSED_AXIS.md).
  // Their own pair, because Check/X already name Closed and Cancelled in the
  // same "Add a state" sheet.
  Scales, // Review… - the judgment itself, before it picks a side
  ThumbsUp, // Accepted
  ThumbsDown, // Denied
  ArrowsDownUp, // sort control (pick a sort key: the nav Views list, the Work list)
  ArrowDown, // descending sort direction (paired with ArrowUp for ascending)
  Rows, // group-by control (pick the sectioning dimension on the Work list)
  // work-list sort/group option glyphs (leading icons in the arrangement menus)
  PencilSimpleLine, // sort: Last updated (a field on the item changed); the Proposed changes door on the phone item page
  Pulse, // sort: Last activity (any log event, any actor); the Activity door on the phone item page
  Sparkle, // sort: Newest (recently created)
  Flag, // sort + group: Priority
  TextAa, // sort: A–Z (alphabetical)
  Star, // favorite/sticky a view (nav Favorites); weight="fill" when starred
  PushPin, // the nav "pin a view" control (surface word for the internal star); weight="fill" when pinned
  Link, // copy link to an item; the prose editor's link
  Handshake, // "make this a promise": promote a dependency to a boundary contract
  // map / canvas controls
  Plus, // zoom in; an added value (the values kit's change)
  Minus, // zoom out; a removed value (the values kit's change)
  CornersOut, // fit to view
  ArrowCounterClockwise, // reset view
  Warning, // deadlock / attention marker
  // map node actions / view state
  TreeStructure, // focus a decomposition subtree; the Relationships door on the phone item page
  EyeSlash, // hide a node from the view
  Eye, // restore hidden nodes; the content head's Display menu (show closed)
  FloppyDisk, // save the current map view; persist the ephemeral layer ("Update lens")
  GitFork, // mint a new lens from the ephemeral layer ("Fork" / birth's "Save as lens")
  Users, // a group-shared saved view
  UsersThree, // a view's audience (the slice-4 dial)
  ShareNetwork, // mint a shared view from a source (docs/VIEWS.md §5)
  HandArrowUp, // share the work: the §7 bulk item grant (docs/VIEWS.md §7)
  User, // a person (quick-open's people targets)
  Faders, // configure a board's lens (lanes + fields)
  FadersHorizontal, // the content head's compact arrange trigger (folds the cluster into a sheet)
  Funnel, // the filter family: the map filter card; the Custom grouping (a predicate per group)
  FlowArrow, // Flows (the flow authoring surface)
  PencilSimple, // open a definition row for editing (authoring surfaces)
  BookOpen, // expand/collapse a definition card to read its detail (stage editor)
  Archive, // archive a flow (durability: retire without deleting)
  ArrowUUpLeft, // "take this back": un-archive a flow; Retract a delivery, Resolve a verdict, Reopen a conclusion
  Hash, // the user tags (docs/FIELDS_AND_TAGS.md §6): the Tags page, group by tag, a tag value's sign; the prose editor's "name an item"
  // contacts (docs/REACHABILITY.md §2)
  UserMinus, // remove a contact
  Prohibit, // block a person's contact requests; group by: No grouping (a picker's "none")
  // agents (the account page's one list of agent grants, docs/AGENT_ACCESS.md §6)
  PlugsConnected, // a connector: an app connected through /connect
  Key, // an agent key
  // the values kit's own signs (src/components/values/, one sign per data type)
  Clock, // a duration
  Shield, // a containment: what this item holds toward a related one
  // appearance (the palette's theme commands)
  Sun, // light theme
  Moon, // dark theme
  Monitor, // follow the system theme
  // the profile menu's rows (docs/DESIGN_SYSTEM.md "The person and the instance")
  UserCircle, // Account: the person's own page
  GearSix, // Admin: the operator's pages for the instance
  CircleHalf, // Appearance: the device's light or dark setting
  UserSwitch, // Persona: the dev persona switcher
  SignOut, // Sign out
  // the operator's account and link acts (src/app/admin)
  Copy, // copy an address or a link shown once
  EnvelopeSimple, // mint an email verification link
  ShieldCheck, // make an account an operator
  ShieldSlash, // remove the operator flag
  Trash, // delete an account
  FileText, // a file on an item that is not an image (docs/ATTACHMENTS.md §5)
  // the phone item page's doors (docs/MOBILE.md §3), the ones no other row lends
  TextAlignLeft, // the Description door
  Paperclip, // the Files door
  Info, // the Details door
  LinkBreak, // revoke an account link
  // a filter chip's operator signs (src/components/search/criterion-chip.tsx)
  NotEquals, // a negated attribute: "Contributor ≠ Blake"
  GreaterThanOrEqual, // health at least a grade
  LessThan, // health below a grade
  Circle, // the picked row of a one-value filter in the chip editor (the level), weight="fill"
  // a task item's read-only box in rendered prose (src/components/markdown.tsx)
  Square, // open
  CheckSquare, // checked, weight="fill"
  // the prose editor's formatting strip (src/components/ui/prose-editor-body.tsx)
  TextB, // bold
  TextItalic, // italic
  TextStrikethrough, // strikethrough
  Code, // inline code
  ListBullets, // a bullet list
  ListNumbers, // a numbered list
  TextH, // a section heading
  Quotes, // a quote
  At, // name a person (docs/PROSE.md §5)
} from '@phosphor-icons/react/dist/ssr';

import { createElement } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import {
  DotsThree as PhDotsThree,
  DotsThreeVertical as PhDotsThreeVertical,
} from '@phosphor-icons/react/dist/ssr';

/**
 * The dots glyphs carry almost no ink at Phosphor's regular weight - three
 * 1.5px specks - so the kebab reads as nearly invisible. Bold is the house
 * default for exactly these two (fatter dots, still no outline); everything
 * else keeps the regular weight. Pass `weight` explicitly to override.
 */
export const DotsThree = (props: IconProps) =>
  createElement(PhDotsThree, { weight: 'bold', ...props });
// overflow kebab
export const DotsThreeVertical = (props: IconProps) =>
  createElement(PhDotsThreeVertical, { weight: 'bold', ...props });
